import * as React from 'react'
import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import styled from 'styled-components'
import { useLocation } from 'react-router-dom'
import { usePercentageBarConfig } from '../../hooks/usePercentageBarConfig'
import { getCachedThresholds, getPercentageBarConfig } from '../../utils/percentageBarConfig'
import { COLORS } from '../../constants'

type TruckVisualizationProps = {
  percentage: number
}

const Container = styled.div`
  position: relative;
  width: 100%;
  background-color: ${COLORS.primary};
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 0 1rem 0;
`

const Title = styled.h2`
  font-size: 2rem;
  font-weight: bold;
  color: ${COLORS.white};
  text-align: center;
`

const TruckContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 50rem;
  margin: 0 auto;
`

const PercentageDisplay = styled.div`
  position: absolute;
  top: 42%;
  transform: translateY(-50%);
  right: -5%;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
`

const PercentageText = styled.span`
  color: ${COLORS.white};
  font-size: 1.5rem;
  font-weight: bold;
`

const Message = styled.div<{ $color: string }>`
  margin-top: 0.5rem;
  font-size: 1.25rem;
  font-weight: 500;
  text-align: center;
  color: ${({ $color }) => {
    switch ($color) {
      case 'red':
        return '#f87171'
      case 'green':
        return '#4ade80'
      case 'yellow':
        return '#fbbf24'
      case 'orange':
        return '#fb923c'
      default:
        return COLORS.gray
    }
  }};
`

const TruckVisualization: React.FC<TruckVisualizationProps> = ({
  percentage
}) => {
  console.log('TruckVisualization rendered with percentage:', percentage)

  const { color, message } = usePercentageBarConfig(percentage)
  const location = useLocation()
  const [thresholds, setThresholds] = useState(getCachedThresholds())
  const [displayPercentage, setDisplayPercentage] = useState(0)

  console.log('TruckVisualization state:', {
    percentage,
    displayPercentage,
    color,
    message,
    thresholds,
    pathname: location.pathname
  })

  // Fetch thresholds immediately on mount if not cached
  useEffect(() => {
    const fetchThresholds = async () => {
      console.log('Fetching thresholds...')
      const cached = getCachedThresholds()
      console.log('Cached thresholds:', cached)

      if (!cached) {
        console.log('No cached thresholds, fetching from API...')
        await getPercentageBarConfig(1, location.pathname)
        const newThresholds = getCachedThresholds()
        console.log('Fetched thresholds:', newThresholds)
        setThresholds(newThresholds)
      } else {
        console.log('Using cached thresholds')
        setThresholds(cached)
      }
    }
    fetchThresholds()
  }, [location.pathname])

  useEffect(() => {
    console.log(`Animating percentage: ${displayPercentage} -> ${percentage}`)

    const duration = 800 // milliseconds
    const steps = 60
    const stepDuration = duration / steps

    // Animate from current displayPercentage to target percentage
    const startValue = displayPercentage
    const change = percentage - startValue
    const increment = change / steps

    if (change === 0) {
      console.log('No animation needed, change is 0')
      return // No animation needed
    }

    let currentStep = 0
    const timer = setInterval(() => {
      currentStep++
      if (currentStep >= steps) {
        setDisplayPercentage(percentage)
        console.log('Animation complete:', percentage)
        clearInterval(timer)
      } else {
        setDisplayPercentage(Math.round(startValue + increment * currentStep))
      }
    }, stepDuration)

    return () => clearInterval(timer)
  }, [percentage])

  // Map color strings to actual color values
  const getColorValue = (colorName: string): string => {
    switch (colorName) {
      case 'red':
        return '#f87171'
      case 'green':
        return '#4ade80'
      case 'yellow':
        return '#fbbf24'
      case 'orange':
        return '#fb923c'
      default:
        return COLORS.gray
    }
  }

  // Calculate half-load range positions (proportional to cargo area width of 376px)
  const cargoWidth = 376
  const halfLoadStart = thresholds
    ? (thresholds.LOWER_PERCENT / 100) * cargoWidth
    : 0
  const halfLoadEnd = thresholds
    ? (thresholds.UPPER_PERCENT / 100) * cargoWidth
    : 0
  const fullLoadStart = thresholds
    ? (thresholds.HIGH_PERCENT / 100) * cargoWidth
    : 0

  return (
    <Container>
      <Title>Fill Percentage</Title>
      <TruckContainer>
        <svg
          viewBox="0 0 600 180"
          style={{ width: '100%', height: 'auto', filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))' }}
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Truck Shadow */}
          <ellipse cx="300" cy="165" rx="280" ry="8" fill="rgba(0,0,0,0.2)" />

          {/* Shorter Hood - More Realistic Proportions */}
          <rect
            x="50"
            y="65"
            width="50"
            height="45"
            fill="#3a4a63"
            stroke="#2a3441"
            strokeWidth="2"
            rx="4"
          />

          {/* Front Grille */}
          <rect x="42" y="68" width="12" height="39" fill="#2a3441" rx="2" />

          {/* Grille Lines */}
          <line
            x1="46"
            y1="73"
            x2="52"
            y2="73"
            stroke="#4a5568"
            strokeWidth="1.5"
          />
          <line
            x1="46"
            y1="78"
            x2="52"
            y2="78"
            stroke="#4a5568"
            strokeWidth="1.5"
          />
          <line
            x1="46"
            y1="83"
            x2="52"
            y2="83"
            stroke="#4a5568"
            strokeWidth="1.5"
          />
          <line
            x1="46"
            y1="88"
            x2="52"
            y2="88"
            stroke="#4a5568"
            strokeWidth="1.5"
          />
          <line
            x1="46"
            y1="93"
            x2="52"
            y2="93"
            stroke="#4a5568"
            strokeWidth="1.5"
          />
          <line
            x1="46"
            y1="98"
            x2="52"
            y2="98"
            stroke="#4a5568"
            strokeWidth="1.5"
          />

          {/* Hood Details */}
          <rect x="55" y="72" width="40" height="4" fill="#4a5568" rx="2" />
          <rect x="55" y="82" width="40" height="4" fill="#4a5568" rx="2" />
          <rect x="55" y="92" width="40" height="4" fill="#4a5568" rx="2" />

          {/* Truck Cab - Rectangular American Style */}
          <rect
            x="100"
            y="45"
            width="65"
            height="65"
            fill="#3a4a63"
            stroke="#2a3441"
            strokeWidth="2"
            rx="4"
          />

          {/* Cab Windshield - Large and Rectangular */}
          <rect
            x="108"
            y="52"
            width="50"
            height="32"
            fill="#4a90e2"
            stroke="#2a3441"
            strokeWidth="1"
            rx="3"
            opacity="0.8"
          />

          {/* Cab Side Window */}
          <rect
            x="108"
            y="88"
            width="50"
            height="16"
            fill="#4a90e2"
            stroke="#2a3441"
            strokeWidth="1"
            rx="2"
            opacity="0.8"
          />

          {/* Exhaust Stacks */}
          <rect x="155" y="30" width="5" height="20" fill="#2a3441" rx="2" />
          <rect x="162" y="30" width="5" height="20" fill="#2a3441" rx="2" />

          {/* Truck Cargo Area Frame - Even Larger */}
          <rect
            x="165"
            y="42"
            width="390"
            height="68"
            fill="#4a5568"
            stroke="#2a3441"
            strokeWidth="3"
            rx="6"
          />

          {/* Cargo Area Interior (Progress Bar Background) */}
          <rect
            x="172"
            y="49"
            width="376"
            height="54"
            fill="#e2e8f0"
            rx="3"
          />

          {/* Half-Load Range Indicator */}
          {thresholds && (
            <rect
              x={172 + halfLoadStart}
              y="49"
              width={halfLoadEnd - halfLoadStart}
              height="54"
              fill="rgba(34, 197, 94, 0.2)"
              rx="3"
            />
          )}

          {/* Full-Load Range Indicator */}
          {thresholds && (
            <rect
              x={172 + fullLoadStart}
              y="49"
              width={cargoWidth - fullLoadStart}
              height="54"
              fill="rgba(34, 197, 94, 0.15)"
              rx="3"
            />
          )}

          {/* Progress Bar Fill Inside Cargo Area */}
          <defs>
            <clipPath id="cargoClip">
              <rect x="172" y="49" width="376" height="54" rx="3" />
            </clipPath>
          </defs>
          <motion.rect
            x="172"
            y="49"
            height="54"
            fill={getColorValue(color)}
            clipPath="url(#cargoClip)"
            initial={{ width: 0 }}
            animate={{
              width: (Math.min(percentage, 100) / 100) * 376
            }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            rx="3"
          />

          {/* Cargo Area Grid Pattern */}
          <defs>
            <pattern
              id="grid"
              width="20"
              height="20"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 20 0 L 0 0 0 20"
                fill="none"
                stroke="#cbd5e0"
                strokeWidth="0.5"
                opacity="0.3"
              />
            </pattern>
          </defs>
          <rect
            x="172"
            y="49"
            width="376"
            height="54"
            fill="url(#grid)"
            clipPath="url(#cargoClip)"
          />

          {/* Range Markers */}
          {thresholds && (
            <>
              {/* Half Load Range Markers */}
              <line
                x1={172 + halfLoadStart}
                y1="49"
                x2={172 + halfLoadStart}
                y2="103"
                stroke="#22c55e"
                strokeWidth="2"
                opacity="0.8"
              />
              <line
                x1={172 + halfLoadEnd}
                y1="49"
                x2={172 + halfLoadEnd}
                y2="103"
                stroke="#22c55e"
                strokeWidth="2"
                opacity="0.8"
              />
              {/* Full Load Range Marker */}
              <line
                x1={172 + fullLoadStart}
                y1="49"
                x2={172 + fullLoadStart}
                y2="103"
                stroke="#22c55e"
                strokeWidth="2"
                opacity="0.6"
              />
              {/* Labels */}
              <text
                x={172 + (halfLoadStart + halfLoadEnd) / 2}
                y="40"
                textAnchor="middle"
                fill="#22c55e"
                fontSize="10"
                fontWeight="bold"
              >
                Half Load
              </text>
              <text
                x={172 + (fullLoadStart + cargoWidth) / 2}
                y="40"
                textAnchor="middle"
                fill="#22c55e"
                fontSize="10"
                fontWeight="bold"
              >
                Full Load
              </text>
            </>
          )}

          {/* Truck Chassis */}
          <rect x="50" y="110" width="505" height="12" fill="#3a4a63" rx="3" />

          {/* Front Wheel */}
          <circle
            cx="85"
            cy="140"
            r="20"
            fill="#2d3748"
            stroke="#1a202c"
            strokeWidth="2"
          />
          <circle cx="85" cy="140" r="12" fill="#4a5568" />
          <circle cx="85" cy="140" r="6" fill="#718096" />

          {/* Drive Wheels */}
          <circle
            cx="135"
            cy="140"
            r="20"
            fill="#2d3748"
            stroke="#1a202c"
            strokeWidth="2"
          />
          <circle cx="135" cy="140" r="12" fill="#4a5568" />
          <circle cx="135" cy="140" r="6" fill="#718096" />

          {/* Trailer Wheels */}
          <circle
            cx="480"
            cy="140"
            r="20"
            fill="#2d3748"
            stroke="#1a202c"
            strokeWidth="2"
          />
          <circle cx="480" cy="140" r="12" fill="#4a5568" />
          <circle cx="480" cy="140" r="6" fill="#718096" />

          <circle
            cx="515"
            cy="140"
            r="20"
            fill="#2d3748"
            stroke="#1a202c"
            strokeWidth="2"
          />
          <circle cx="515" cy="140" r="12" fill="#4a5568" />
          <circle cx="515" cy="140" r="6" fill="#718096" />

          {/* Cab Door Handle */}
          <rect x="148" y="88" width="8" height="3" fill="#718096" rx="1" />

          {/* Cargo Area Door Hinges */}
          <rect x="548" y="52" width="3" height="8" fill="#2a3441" rx="1" />
          <rect x="548" y="65" width="3" height="8" fill="#2a3441" rx="1" />
          <rect x="548" y="90" width="3" height="8" fill="#2a3441" rx="1" />

          {/* Front Bumper */}
          <rect x="38" y="105" width="18" height="8" fill="#2a3441" rx="2" />
        </svg>

        {/* Percentage Display */}
        <PercentageDisplay>
          <PercentageText>
            {isNaN(displayPercentage) ? '0' : displayPercentage.toFixed(0)}%
          </PercentageText>
        </PercentageDisplay>
      </TruckContainer>

      {/* Message */}
      {percentage > 0 && <Message $color={color}>{message}</Message>}
    </Container>
  )
}

export default TruckVisualization
